import React, { useEffect } from "react";
import { useTranslator } from "../../context/translator";
import { useLocation } from "react-router-dom";

const Pagination = ({
  pageNumber,
  totalRecords,
  page,
  setPage,
  isLoading,
  data,
  pageSize = 5,
}) => {
  const location = useLocation();
  const { translate } = useTranslator();

  const handleClick = (newPage) => {
    if (newPage >= 1 && newPage <= pageNumber) {
      setPage(newPage);
    }
  };

  const handleNextPage = () => handleClick(page + 1);
  const handlePreviousPage = () => handleClick(page - 1);

  const renderPageNumbers = () => {
    if (!page || !pageNumber || pageNumber < 1) return null;

    const totalPages = pageNumber;
    const pageNumbers = [];
    const maxPagesToShow = 10;

    const getButtonClass = (i) =>
      `flex items-center justify-center px-4 h-8 leading-tight ${
        page === i
          ? "text-blue-600 border border-gray-300 bg-blue-50"
          : "text-gray-500 bg-white border border-gray-300"
      } hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white`;

    const createButton = (pageB) => (
      <li key={pageB}>
        <button
          onClick={() => handleClick(pageB)}
          className={getButtonClass(pageB)}
        >
          {pageB}
        </button>
      </li>
    );

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(createButton(i));
    } else {
      pageNumbers.push(createButton(1));

      if (page > 4) {
        pageNumbers.push(
          <li key="start-ellipsis">
            <div className="mx-2">...</div>
          </li>
        );
      }

      const startPage = Math.max(2, page - 2);
      const endPage = Math.min(totalPages - 1, page + 2);

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(createButton(i));
      }

      if (page < totalPages - 3) {
        pageNumbers.push(
          <li key="end-ellipsis">
            <div className="mx-2">...</div>
          </li>
        );
      }

      pageNumbers.push(createButton(totalPages));
    }

    return pageNumbers;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = parseInt(searchParams.get("page"));
    if (pageParam && pageParam !== page) {
      setPage(pageParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <>
      {pageNumber > 1 && (
        <nav
          className="flex items-center flex-column flex-wrap md:flex-row justify-between pt-4 mb-2 mx-2"
          aria-label="Table navigation"
        >
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-4 md:mb-0 block w-full md:inline md:w-auto">
            {translate("showing")}{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              {data?.length <= pageSize ? data?.length : pageSize}
            </span>{" "}
            {translate("of")}{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              {totalRecords}
            </span>
          </span>

          <ul className="inline-flex -space-x-px rtl:space-x-reverse text-sm h-8">
            <li>
              <button
                className="flex items-center justify-center px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-50"
                onClick={handlePreviousPage}
                disabled={isLoading || page === 1}
              >
                {translate("previous")}
              </button>
            </li>

            {renderPageNumbers()}

            <li>
              <button
                type="button"
                className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-50"
                onClick={handleNextPage}
                disabled={isLoading || pageNumber === page}
              >
                {translate("next")}
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
};

export default Pagination;
